import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth';
import { RowsPhotoAlbum } from 'react-photo-album';
import 'react-photo-album/rows.css';
import type { PricingType, Profile, Room, SessionArtwork, UpcomingSession } from '@modelcast/types';
import { SecureVideoPlayer } from '../../components/SecureVideoPlayer';
import { NavBar } from '../../components/NavBar';
import { SharedFooter } from '../../components/SharedFooter';
import { TipForms } from './TipForm';
import { FollowButton } from '../follow/FollowButton';
import { DateTimePicker } from '../create/DateTimePicker';
import { useCopy } from '../../shared/copy/CopyProvider';
import { authHeaders, jsonAuthHeaders, signInMessageForAuthStatus } from '../../shared/authHeaders';
import { getReferencePackThumbnailUrl } from '../reference-packs/referencePackImages';
import { canCreateReferencePackFromSummary } from './sessionEndedPermissions';
import { ModelSafetyCheckGate, PostSessionReputationPanel } from '../reputation/PostSessionReputationPanel';
import { formatMoney, formatMoneyCompact } from '../../shared/money';

// Recording with hasVideo flag (API no longer returns s3Url/s3Key for security)
interface SecureRecording {
  id: string;
  roomId: string;
  label?: string | null;
  hasVideo: boolean;
  contentType?: string | null;
  durationSeconds: number | null;
  startedAt: string | null;
  status: string;
  releaseStatus?: 'pending_review' | 'released' | 'rejected';
  canView?: boolean;
  priceCents?: number | null;
  currency?: string;
}

interface SessionProduct {
  id: string;
  sellerProfileId?: string | null;
  modelProfileId?: string | null;
  title: string;
  description: string | null;
  accessType: 'sellable' | 'free' | 'private';
  priceCents: number | null;
  currency: string;
  status: 'draft' | 'ready';
  coverImageUrl: string | null;
  accessExpiresOn: string | null;
  accessDurationCount: number | null;
  accessDurationUnit: 'days' | 'weeks' | 'months' | 'years' | null;
  purchaseAccessExpiresOn?: string | null;
  captureIds: string[];
  recordingIds: string[];
  requiresBuyerApproval?: boolean;
  purchased?: boolean;
  canEdit?: boolean;
  accessRequest?: ReferencePackAccessRequestSummary | null;
  captures?: SessionProductCapture[];
}

interface SessionProductCapture {
  id: string;
  imageUrl: string;
  thumbnailUrl?: string | null;
  width: number | null;
  height: number | null;
}

interface ReferencePackAccessRequestSummary {
  id: string;
  status: 'pending' | 'approved' | 'denied' | 'purchased';
  requestedAccessExpiresOn: string | null;
  requestedPriceCents: number | null;
  approvedPriceCents: number | null;
  approvedCurrency: string | null;
  sellerNote: string | null;
  decidedAt: string | null;
  updatedAt: string | null;
}

interface ProductReleaseDetails {
  intendedUse: string;
  accessExpiresOn: string;
}

function getReferencePackAccessDurationLabel(
  count: number | null | undefined,
  unit: 'days' | 'weeks' | 'months' | 'years' | null | undefined
) {
  if (!count || count <= 0 || !unit) return null;
  const singularUnit = unit.replace(/s$/, '');
  return `${count} ${count === 1 ? singularUnit : unit}`;
}

interface SessionData {
  room: Room;
  hostProfile: Profile | null;
  modelProfile: Profile | null;
  modelProfiles: Profile[];
  attendees: SummaryAttendee[];
  viewerIsHost: boolean;
  viewerIsModel: boolean;
  viewerHadTicket: boolean;
  artworkCount: number;
  artwork: SessionArtwork[];
  recordings: SecureRecording[];
  products: SessionProduct[];
}

interface SettlementPayout {
  role: string;
  amount: number;
  status: string;
  stripeTransferId: string | null;
  createdAt: string | null;
  completedAt: string | null;
  stripeAvailableOn: string | null;
}

interface SettlementStatus {
  isSettled: boolean;
  totalRevenue: number;
  platformFee: number;
  netRevenue: number;
  expectedHostPayout: number;
  expectedModelPayout: number;
  modelGuarantee: number;
  modelSplitPercent: number;
  revenueSplitMode?: 'manual' | 'even';
  payouts: SettlementPayout[];
}

interface FinancialPayment {
  id: string;
  userId: string;
  inviteId: string | null;
  buyerName: string;
  buyerEmail: string | null;
  amount: number;
  platformFee: number;
  netAmount: number;
  currency: string;
  status: string;
  stripePaymentIntentId: string | null;
  stripeRefundId: string | null;
  refundRequestStatus: 'pending' | 'handled' | null;
  refundRequestOutcome: string | null;
  createdAt: string | null;
  completedAt: string | null;
  refundedAt: string | null;
}

// Cancelled tickets are not active sales: a pending request awaits the
// organizer's decision, a declined request keeps the money with the event.
function paymentStatusLabel(payment: FinancialPayment): { label: string; tone: 'warn' | 'accent' | 'dim' } {
  if (payment.status === 'refunded') return { label: 'Refunded', tone: 'warn' };
  if (payment.refundRequestStatus === 'pending') return { label: 'Refund requested', tone: 'warn' };
  if (payment.refundRequestStatus === 'handled' && payment.refundRequestOutcome === 'declined') {
    return { label: 'Refund declined', tone: 'dim' };
  }
  if (payment.refundRequestStatus === 'handled' && payment.refundRequestOutcome === 'manual_refund_recorded') {
    return { label: 'Refund recorded', tone: 'warn' };
  }
  return { label: 'Paid', tone: 'accent' };
}

interface FinancialPayout {
  id: string;
  role: string;
  recipientUserId: string;
  recipientName: string;
  amount: number;
  currency: string;
  status: string;
  stripeTransferId: string | null;
  errorMessage: string | null;
  createdAt: string | null;
  completedAt: string | null;
}

interface EventFinancialSummary {
  roomId: string;
  currency: string;
  pricingType: PricingType;
  hasEnded: boolean;
  refundsDisabled: boolean;
  refundsDisabledReason: string | null;
  canSendPayouts: boolean;
  payoutsStarted: boolean;
  payoutsPending: boolean;
  payoutsFailed: boolean;
  hasBlockingRefundRequests: boolean;
  payoutReleaseDueAt: string | null;
  payoutReleaseStartedAt: string | null;
  payoutReleaseTrigger: 'manual' | 'automatic' | 'pending_account_retry' | null;
  totals: {
    grossRevenue: number;
    refundedAmount: number;
    activeRevenue: number;
    platformFee: number;
    netRevenue: number;
    completedPaymentCount: number;
    refundedPaymentCount: number;
  };
  payments: FinancialPayment[];
  payouts: FinancialPayout[];
  settlement: SettlementStatus;
}

interface SettlementResponse {
  hostPayoutStatus?: 'completed' | 'pending' | 'failed';
  modelPayoutStatus?: 'completed' | 'pending' | 'failed' | 'none';
  error?: string;
}

interface SummaryAttendee {
  name: string | null;
  initial: string;
  photoUrl: string | null;
  profileId: string | null;
}

function getDuplicateDateTimeMin() {
  const date = new Date(Date.now() + 5 * 60 * 1000);
  date.setSeconds(0, 0);
  return formatDateTimeLocal(date);
}

function formatDateTimeLocal(date: Date) {
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function getDefaultDuplicateDateTime(scheduledAt: string | null) {
  const sourceDate = scheduledAt ? new Date(scheduledAt) : null;
  if (!sourceDate || !Number.isFinite(sourceDate.getTime())) {
    return getDuplicateDateTimeMin();
  }

  const now = new Date();
  const candidate = new Date(now);
  candidate.setHours(
    sourceDate.getHours(),
    sourceDate.getMinutes(),
    0,
    0
  );

  const daysUntilSameWeekday = (sourceDate.getDay() - now.getDay() + 7) % 7;
  candidate.setDate(now.getDate() + daysUntilSameWeekday);

  while (candidate.getTime() <= now.getTime()) {
    candidate.setDate(candidate.getDate() + 7);
  }

  return formatDateTimeLocal(candidate);
}

function formatCents(cents: number, currency?: string | null) {
  if (cents <= 0) return 'Free';
  return formatMoneyCompact(cents, currency);
}

function formatDate(dateString: string | null) {
  if (!dateString) return null;
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getPayoutDetail(
  row: { completedAt: string | null; status: string; amount: number; errorMessage: string | null },
  payoutsSent: boolean
) {
  if (row.completedAt) {
    return `Sent through Stripe on ${formatDate(row.completedAt)}.`;
  }

  if (payoutsSent) {
    return 'Stripe transfer has been created for this event.';
  }

  if (row.errorMessage) {
    return row.errorMessage;
  }

  if (row.status === 'pending') {
    return 'Waiting for Stripe payout setup or a retry.';
  }

  if (row.status === 'failed') {
    return 'Stripe could not send this payout. Retry after the issue is fixed.';
  }

  return row.amount > 0 ? 'Not sent yet.' : 'No payout due.';
}

const emptyProductReleaseDetails: ProductReleaseDetails = {
  intendedUse: '',
  accessExpiresOn: '',
};

function ProfileCard({
  profile,
  role,
  children,
}: {
  profile: Profile;
  role: 'host' | 'model' | 'host-model';
  children?: React.ReactNode;
}) {
  const primaryPhoto = profile.photos[0]?.url;
  const roleLabel = role === 'host-model'
    ? 'Organizer and model'
    : role === 'host'
      ? 'Organizer'
      : 'Model';
  const roleClassName = role === 'host-model'
    ? 'bg-accent-soft text-accent'
    : role === 'host'
      ? 'bg-accent-soft text-accent'
      : 'bg-tag-soft text-tag';

  return (
    <div className="bg-panel border border-line rounded-xl p-4">
      <div className="flex items-start gap-4">
        {/* Avatar */}
        <Link to={`/profile/${profile.id}`}>
          {primaryPhoto ? (
            <img
              src={primaryPhoto}
              alt={profile.displayName}
              className="w-14 h-14 rounded-full object-cover"
            />
          ) : (
            <div className="w-14 h-14 rounded-full bg-panel2 flex items-center justify-center text-text-dim text-lg font-medium">
              {profile.displayName[0]?.toUpperCase()}
            </div>
          )}
        </Link>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-start gap-x-2 gap-y-1 mb-1">
            <Link
              to={`/profile/${profile.id}`}
              className="min-w-0 max-w-full break-words font-medium text-text hover:text-accent transition-colors"
            >
              {profile.displayName}
            </Link>
            <span className={`pill shrink-0 text-[10px] ${roleClassName}`}>
              {roleLabel}
            </span>
          </div>

          {profile.bio && (
            <p className="text-sm text-text-dim line-clamp-2 mb-2">{profile.bio}</p>
          )}

          <div className="flex items-center gap-2">
            <FollowButton profileId={profile.id} />
          </div>
        </div>
      </div>

      {children}
    </div>
  );
}

function AttendeesSummary({ attendees }: { attendees: SummaryAttendee[] }) {
  const attendeeLabel = attendees.length === 1 ? '1 attendee' : `${attendees.length} attendees`;

  return (
    <section className="bg-panel border border-line rounded-xl p-6 mb-8">
      <div className="mb-5">
        <h2 className="font-display text-xl font-normal">Attendees</h2>
        <p className="text-sm text-text-dim mt-1">{attendeeLabel}</p>
      </div>

      {attendees.length === 0 ? (
        <p className="text-sm text-text-dim">No attendees recorded for this event.</p>
      ) : (
        <div className="flex flex-wrap gap-3">
          {attendees.map((attendee, index) => {
            const content = (
              <>
                {attendee.photoUrl ? (
                  <img
                    src={attendee.photoUrl}
                    alt={attendee.name || 'Attendee'}
                    className="w-9 h-9 rounded-full object-cover"
                  />
                ) : (
                  <span className="w-9 h-9 rounded-full bg-panel2 border border-line flex items-center justify-center font-display text-[14px] text-accent">
                    {attendee.initial || '?'}
                  </span>
                )}
                <span className="text-sm font-medium text-text">
                  {attendee.name || 'Guest'}
                </span>
              </>
            );

            const className = "inline-flex items-center gap-2.5 rounded-full bg-bg-secondary border border-line-soft pl-1.5 pr-3 py-1.5";

            return attendee.profileId ? (
              <Link
                key={attendee.profileId}
                to={`/profile/${attendee.profileId}`}
                className={`${className} hover:bg-panel2 transition-colors`}
              >
                {content}
              </Link>
            ) : (
              <div key={`${attendee.name || 'guest'}-${index}`} className={className}>
                {content}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function ReferencePackPhotoMasonry({ product }: { product: SessionProduct }) {
  const captures = product.captures ?? [];
  const photos = captures.length > 0
    ? captures.map((capture) => ({
        src: getReferencePackThumbnailUrl(capture),
        width: 1,
        height: 1,
        key: capture.id,
        alt: '',
      }))
    : product.coverImageUrl
      ? [{
          src: product.coverImageUrl,
          width: 1,
          height: 1,
          key: `${product.id}-cover`,
          alt: '',
        }]
      : [];

  if (photos.length === 0) return null;

  return (
    <div className="mb-4 overflow-hidden rounded bg-black">
      <RowsPhotoAlbum
        photos={photos}
        targetRowHeight={112}
        spacing={6}
        rowConstraints={{ maxPhotos: 4, singleRowMaxHeight: 150 }}
        render={{
          image: (props) => (
            <img
              {...props}
              className="react-photo-album--image object-cover"
              style={{ ...props.style, objectFit: 'cover' }}
            />
          ),
        }}
      />
    </div>
  );
}

function FinancialSummaryPanel({
  summary,
  loading,
  error,
  notice,
  viewerIsHost,
  viewerIsModel,
  refundingPaymentId,
  settlingPayouts,
  onRefresh,
  onRefundPayment,
  onSendPayouts,
}: {
  summary: EventFinancialSummary | null;
  loading: boolean;
  error: string | null;
  notice: string | null;
  viewerIsHost: boolean;
  viewerIsModel: boolean;
  refundingPaymentId: string | null;
  settlingPayouts: boolean;
  onRefresh: () => void;
  onRefundPayment: (payment: FinancialPayment) => void;
  onSendPayouts: () => void;
}) {
  if (loading && !summary) {
    return (
      <section className="bg-panel border border-line rounded-xl p-6 mb-8">
        <div className="text-sm text-text-dim">Loading financial summary...</div>
      </section>
    );
  }

  if (error && !summary) {
    return (
      <section className="bg-panel border border-line rounded-xl p-6 mb-8">
        <h2 className="font-display text-xl font-normal">Financial summary</h2>
        <p className="text-sm text-warn mt-2">{error}</p>
        <button type="button" onClick={onRefresh} className="btn btn-sm mt-4">
          Try again
        </button>
      </section>
    );
  }

  if (!summary) {
    return null;
  }

  const hostPayout = summary.payouts.find((payout) => payout.role === 'host');
  const modelPayout = summary.payouts.find((payout) => payout.role === 'model');
  const expectedPayoutTotal = summary.settlement.expectedHostPayout + summary.settlement.expectedModelPayout;
  const hasMoneyToPayOut = summary.totals.activeRevenue > 0 || expectedPayoutTotal > 0 || summary.payouts.length > 0;
  const showHostFinancials = viewerIsHost;
  const showModelFinancials = viewerIsModel || viewerIsHost;
  const isFreeEvent = summary.pricingType === 'free';
  const showSettlementTerms = !isFreeEvent;
  const ticketSummaryLabel = isFreeEvent
    ? 'Free event'
    : summary.totals.completedPaymentCount > 0
      ? `${summary.totals.completedPaymentCount} paid ticket${summary.totals.completedPaymentCount === 1 ? '' : 's'}`
      : 'No paid tickets';
  const payoutRows = [
    {
      label: 'Funds to organizer',
      recipientName: hostPayout?.recipientName ?? 'Organizer',
      amount: hostPayout?.amount ?? summary.settlement.expectedHostPayout,
      status: hostPayout?.status ?? 'not sent',
      errorMessage: hostPayout?.errorMessage ?? null,
      completedAt: hostPayout?.completedAt ?? null,
      show: showHostFinancials,
    },
    {
      label: viewerIsModel && !viewerIsHost ? 'Your earnings' : 'Funds to model',
      recipientName: modelPayout?.recipientName ?? 'Model',
      amount: modelPayout?.amount ?? summary.settlement.expectedModelPayout,
      status: modelPayout?.status ?? 'not sent',
      errorMessage: modelPayout?.errorMessage ?? null,
      completedAt: modelPayout?.completedAt ?? null,
      show: showModelFinancials,
    },
  ].filter((row) => row.show && (row.amount > 0 || summary.payouts.length > 0));
  const payoutsSent = summary.refundsDisabled;
  const sendPayoutsLabel = settlingPayouts
    ? 'Sending payouts...'
    : payoutsSent
      ? 'Payouts sent'
      : !hasMoneyToPayOut
        ? 'No payouts'
        : !summary.hasEnded
          ? 'Payouts after event'
          : summary.payoutsStarted || summary.payoutsPending || summary.payoutsFailed
            ? 'Retry payouts'
            : 'Send payouts';

  return (
    <div className="space-y-8 mb-8">
      <section className="bg-panel border border-line rounded-xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4 justify-between mb-5">
          <div>
            <h2 className="font-display text-xl font-normal">Financial summary</h2>
            <p className="text-sm text-text-dim mt-1">
              {ticketSummaryLabel}
              {summary.totals.refundedPaymentCount > 0 ? `, ${summary.totals.refundedPaymentCount} refunded` : ''}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={onRefresh} disabled={loading} className="btn btn-sm disabled:opacity-50">
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
            {viewerIsHost && (
              <button
                type="button"
                onClick={onSendPayouts}
                disabled={!summary.canSendPayouts || settlingPayouts}
                className="btn btn-sm btn-accent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendPayoutsLabel}
              </button>
            )}
          </div>
        </div>

        {notice && (
          <p className="text-sm text-text-dim mb-4">{notice}</p>
        )}

        {error && (
          <p className="text-sm text-warn mb-4">{error}</p>
        )}

        {viewerIsHost && !payoutsSent && (summary.payoutsPending || summary.payoutsFailed) && (
          <div className="rounded-lg border border-line-soft bg-bg-secondary px-4 py-3 text-sm text-text-dim mb-5">
            Some payouts are not complete yet. Check the payout summary below, fix the Stripe requirement, then retry.
          </div>
        )}

        {viewerIsHost && !payoutsSent && !summary.payoutReleaseStartedAt && summary.payoutReleaseDueAt && (
          <div className="rounded-lg border border-line-soft bg-bg-secondary px-4 py-3 text-sm text-text-dim mb-5">
            {summary.hasBlockingRefundRequests
              ? 'Automatic payout release is paused while a refund request needs your review.'
              : `Release payouts when you are ready. If you take no action, they will be released automatically on ${new Date(summary.payoutReleaseDueAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}.`}
          </div>
        )}

        {viewerIsHost && summary.refundsDisabledReason && (
          <div className="rounded-lg border border-line-soft bg-bg-secondary px-4 py-3 text-sm text-text-dim mb-5">
            {summary.refundsDisabledReason} Refunds are locked.
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="rounded-lg bg-bg-secondary border border-line-soft p-3">
            <div className="text-[11px] text-text-faint">Ticket sales</div>
            <div className="font-display text-[22px] text-text">{formatMoney(summary.totals.grossRevenue, summary.currency)}</div>
          </div>
          <div className="rounded-lg bg-bg-secondary border border-line-soft p-3">
            <div className="text-[11px] text-text-faint">Refunded</div>
            <div className="font-display text-[22px] text-text">{formatMoney(summary.totals.refundedAmount, summary.currency)}</div>
          </div>
          <div className="rounded-lg bg-bg-secondary border border-line-soft p-3">
            <div className="text-[11px] text-text-faint">Platform fees</div>
            <div className="font-display text-[22px] text-text">{formatMoney(summary.totals.platformFee, summary.currency)}</div>
          </div>
          <div className="rounded-lg bg-bg-secondary border border-line-soft p-3">
            <div className="text-[11px] text-text-faint">Net for payouts</div>
            <div className="font-display text-[22px] text-text">{formatMoney(summary.totals.netRevenue, summary.currency)}</div>
          </div>
        </div>
      </section>

      {viewerIsModel && showSettlementTerms && (
        <section className="bg-panel border border-line rounded-xl p-6">
          <h2 className="font-display text-xl font-normal mb-4">Model agreement</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-lg bg-bg-secondary border border-line-soft p-4">
              <div className="text-[11px] text-text-faint">Agreed split</div>
              <div className="font-display text-[26px] text-text mt-1">
                {summary.settlement.revenueSplitMode === 'even'
                  ? 'Even split'
                  : `${summary.settlement.modelSplitPercent}% model`}
              </div>
              <p className="text-xs text-text-dim mt-2">
                {summary.settlement.revenueSplitMode === 'even'
                  ? 'Each payout recipient receives an equal share of net revenue.'
                  : `Organizer share: ${100 - summary.settlement.modelSplitPercent}%.`}
              </p>
            </div>
            <div className="rounded-lg bg-bg-secondary border border-line-soft p-4">
              <div className="text-[11px] text-text-faint">Guarantee</div>
              <div className="font-display text-[26px] text-text mt-1">
                {summary.settlement.modelGuarantee > 0
                  ? formatMoney(summary.settlement.modelGuarantee, summary.currency)
                  : 'No guarantee'}
              </div>
              <p className="text-xs text-text-dim mt-2">
                {summary.settlement.modelGuarantee > 0
                  ? 'Minimum agreed model payout for this event.'
                  : 'This event was agreed without a guaranteed minimum.'}
              </p>
            </div>
          </div>
        </section>
      )}

      <section className="bg-panel border border-line rounded-xl p-6">
        <h2 className="font-display text-xl font-normal mb-4">Ticket buyers</h2>
        {summary.payments.length === 0 ? (
          <p className="text-sm text-text-dim">
            {isFreeEvent
              ? 'This event was free, so there are no paid ticket purchases to show.'
              : 'No paid ticket purchases for this event.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-line-soft text-left text-[11px] uppercase text-text-faint">
                  <th className="py-2 pr-4 font-medium">Buyer</th>
                  <th className="py-2 pr-4 font-medium">Paid</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium text-right">Amount</th>
                  {viewerIsHost && <th className="py-2 font-medium text-right">Refund</th>}
                </tr>
              </thead>
              <tbody>
                {summary.payments.map((payment) => {
                  const isRefunded = payment.status === 'refunded';
                  const refundDisabled = summary.refundsDisabled
                    || isRefunded
                    || refundingPaymentId === payment.id
                    || payment.refundRequestStatus === 'pending';
                  const statusInfo = paymentStatusLabel(payment);
                  return (
                    <tr key={payment.id} className="border-b border-line-soft last:border-0">
                      <td className="py-3 pr-4">
                        <div className="font-medium text-text whitespace-nowrap">{payment.buyerName}</div>
                        {payment.buyerEmail && (
                          <div className="text-xs text-text-dim whitespace-nowrap">{payment.buyerEmail}</div>
                        )}
                      </td>
                      <td className="py-3 pr-4 text-text-dim whitespace-nowrap">
                        {formatDate(payment.completedAt ?? payment.createdAt) ?? 'Unknown'}
                      </td>
                      <td className="py-3 pr-4">
                        <span className={`pill text-[10px] ${
                          statusInfo.tone === 'warn'
                            ? 'bg-warn/10 text-warn'
                            : statusInfo.tone === 'dim'
                              ? 'bg-panel2 text-text-dim'
                              : 'pill-accent'
                        }`}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-right font-medium text-text whitespace-nowrap">
                        {formatMoney(payment.amount, payment.currency)}
                      </td>
                      {viewerIsHost && (
                        <td className="py-3 text-right">
                          <button
                            type="button"
                            onClick={() => onRefundPayment(payment)}
                            disabled={refundDisabled}
                            className="btn btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {refundingPaymentId === payment.id
                              ? 'Refunding...'
                              : isRefunded
                                ? 'Refunded'
                                : 'Refund'}
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="bg-panel border border-line rounded-xl p-6">
        <h2 className="font-display text-xl font-normal mb-4">Payout summary</h2>
        {payoutRows.length === 0 ? (
          <div className="rounded-lg bg-bg-secondary border border-line-soft p-4 text-sm text-text-dim">
            {isFreeEvent
              ? 'This event was free, so no ticket revenue payouts were created.'
              : 'No payouts were created because there was no paid revenue for this event.'}
          </div>
        ) : (
          <div className="space-y-3">
            {payoutRows.map((row) => (
            <div key={row.label} className="rounded-lg bg-bg-secondary border border-line-soft p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-medium text-text">{row.label}</div>
                  <div className="text-xs text-text-dim mt-1">
                    {row.recipientName} · Status: {row.status}
                  </div>
                </div>
                <div className="font-display text-2xl text-text">{formatMoney(row.amount, summary.currency)}</div>
              </div>
              <div className="text-xs text-text-dim mt-3">
                {getPayoutDetail(row, payoutsSent)}
              </div>
            </div>
            ))}
          </div>
        )}
        {viewerIsHost && showSettlementTerms && summary.settlement.modelGuarantee > 0 && (
          <p className="text-xs text-text-dim mt-4">
            Model guarantee: {formatMoney(summary.settlement.modelGuarantee, summary.currency)}. Split: {summary.settlement.revenueSplitMode === 'even' ? 'even' : `${summary.settlement.modelSplitPercent}% model`}.
          </p>
        )}
      </section>
    </div>
  );
}

function UpcomingSessionsList({
  profileId,
  roles,
  title = 'Upcoming sessions',
}: {
  profileId: string;
  roles: Array<'hosting' | 'modeling'>;
  title?: string;
}) {
  const [sessions, setSessions] = useState<UpcomingSession[]>([]);
  const [loading, setLoading] = useState(true);
  const rolesKey = roles.join('|');

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const response = await fetch(`/api/profiles/${profileId}/sessions`);
        if (response.ok) {
          const data = await response.json();
          const allowedRoles = rolesKey.split('|');
          const filtered: UpcomingSession[] = data.sessions.filter((session: UpcomingSession) => {
            if (!allowedRoles.includes(session.role)) return false;
            if (session.role !== 'hosting') return true;
            return session.room.isPublished && session.room.isPublic;
          });
          const uniqueSessions = Array.from(
            new Map<string, UpcomingSession>(filtered.map((session) => [session.room.id, session])).values()
          );
          setSessions(uniqueSessions.slice(0, 5));
        }
      } catch (error) {
        console.error('Error fetching sessions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
  }, [profileId, rolesKey]);

  return (
    <div className="mt-4 pt-4 border-t border-line-soft">
      <h3 className="font-mono text-[10px] text-text-faint uppercase mb-2">
        {title}
      </h3>
      {loading ? (
        <div className="text-sm text-text-dim">Loading sessions...</div>
      ) : sessions.length === 0 ? (
        <p className="text-sm text-text-dim">No upcoming sessions listed yet.</p>
      ) : (
        <div className="space-y-2">
          {sessions.map((session) => (
            <Link
              key={session.room.id}
              to={`/s/${session.room.code}`}
              className="block p-3 bg-bg-secondary rounded-lg hover:bg-panel2 transition-colors"
            >
              <div className="font-medium text-sm text-text">{session.room.name}</div>
              <div className="text-xs text-text-dim mt-1">
                {session.room.scheduledAt
                  ? new Date(session.room.scheduledAt).toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })
                  : 'Open session'}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function getReadableCreditName(values: Array<string | null | undefined>) {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) return trimmed;
  }
  return null;
}

function getArtworkArtistName(artwork: SessionArtwork) {
  return getReadableCreditName([artwork.artistProfile?.displayName, artwork.participantName]);
}

function getArtworkModelName(artwork: SessionArtwork) {
  return getReadableCreditName([artwork.modelProfile?.displayName, artwork.modelName]);
}

function getArtworkCreditLabel(artwork: SessionArtwork) {
  return [artwork.medium]
    .map((value) => value?.trim())
    .filter(Boolean)
    .join(' · ');
}

function ArtworkCredit({
  artwork,
  className = '',
}: {
  artwork: SessionArtwork;
  className?: string;
}) {
  const artistName = getArtworkArtistName(artwork);
  const modelName = getArtworkModelName(artwork);
  const creditLabel = getArtworkCreditLabel(artwork);

  if (!artistName && !modelName && !creditLabel) return null;

  return (
    <figure className={['hero-credit', 'session-gallery-credit', className].filter(Boolean).join(' ')} aria-label="Artwork credits">
      {creditLabel && <span className="hc-eyebrow">{creditLabel}</span>}
      {(artistName || modelName) && (
        <div className="hc-people">
          {artistName && (
            <div>
              <span className="role">Artist</span>
              <strong>{artistName}</strong>
            </div>
          )}
          {modelName && (
            <div>
              <span className="role">Model</span>
              <strong>{modelName}</strong>
            </div>
          )}
        </div>
      )}
    </figure>
  );
}

function SessionGallerySummary({
  artwork,
}: {
  artwork: SessionArtwork[];
}) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const featuredArtwork = artwork[0];
  const thumbnailArtwork = artwork.slice(1, 7);
  const selectedArtwork = selectedIndex === null ? null : artwork[selectedIndex] ?? null;

  const openLightbox = (index: number) => {
    setSelectedIndex(Math.max(0, Math.min(index, artwork.length - 1)));
  };

  const closeLightbox = () => {
    setSelectedIndex(null);
  };

  const showNextArtwork = () => {
    setSelectedIndex((current) => {
      if (current === null) return current;
      return (current + 1) % artwork.length;
    });
  };

  const showPreviousArtwork = () => {
    setSelectedIndex((current) => {
      if (current === null) return current;
      return (current - 1 + artwork.length) % artwork.length;
    });
  };

  useEffect(() => {
    if (selectedIndex === null) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedIndex(null);
      } else if (event.key === 'ArrowRight') {
        setSelectedIndex((current) => {
          if (current === null) return current;
          return (current + 1) % artwork.length;
        });
      } else if (event.key === 'ArrowLeft') {
        setSelectedIndex((current) => {
          if (current === null) return current;
          return (current - 1 + artwork.length) % artwork.length;
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedIndex, artwork.length]);

  if (!featuredArtwork) return null;

  const featuredArtistName = getArtworkArtistName(featuredArtwork);
  const selectedArtistName = selectedArtwork ? getArtworkArtistName(selectedArtwork) : null;

  return (
    <section className="bg-panel border border-line rounded-xl p-6 mb-8">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between mb-5">
        <div>
          <h2 className="font-display text-xl font-normal">Session Gallery</h2>
          <p className="text-sm text-text-dim mt-1">
            {artwork.length} artwork{artwork.length !== 1 ? 's' : ''} created during this session
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1.5fr_1fr] gap-3">
        <button
          type="button"
          onClick={() => openLightbox(0)}
          className="group relative block aspect-[4/3] overflow-hidden rounded-lg border border-line-soft bg-bg-secondary p-0 text-left cursor-zoom-in"
          aria-label={featuredArtistName ? `Open artwork by ${featuredArtistName}` : 'Open session artwork'}
        >
          <img
            src={featuredArtwork.imageUrl}
            alt={featuredArtistName ? `Artwork by ${featuredArtistName}` : 'Session artwork'}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
          />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/45 to-transparent p-4">
            <ArtworkCredit artwork={featuredArtwork} />
          </div>
        </button>

        <div className="grid grid-cols-3 sm:grid-cols-2 gap-3">
          {thumbnailArtwork.map((item, index) => {
            const artistName = getArtworkArtistName(item);
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => openLightbox(index + 1)}
                className="group relative block aspect-square overflow-hidden rounded-lg border border-line-soft bg-bg-secondary p-0 cursor-zoom-in"
                aria-label={artistName ? `Open artwork by ${artistName}` : 'Open session artwork'}
              >
                <img
                  src={item.imageUrl}
                  alt={artistName ? `Artwork by ${artistName}` : 'Session artwork'}
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                />
              </button>
            );
          })}
          {artwork.length > 7 && (
            <button
              type="button"
              onClick={() => openLightbox(7)}
              className="flex aspect-square items-center justify-center rounded-lg border border-line-soft bg-bg-secondary text-sm font-medium text-text-dim hover:text-text"
            >
              +{artwork.length - 7} more
            </button>
          )}
        </div>
      </div>

      {selectedArtwork && selectedIndex !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Session gallery artwork"
          onClick={closeLightbox}
        >
          <div className="relative flex h-full w-full max-w-6xl items-center justify-center" onClick={(event) => event.stopPropagation()}>
            <img
              src={selectedArtwork.imageUrl}
              alt={selectedArtistName ? `Artwork by ${selectedArtistName}` : 'Session artwork'}
              className="max-h-full max-w-full object-contain"
            />

            <button
              type="button"
              onClick={closeLightbox}
              className="absolute right-3 top-3 rounded-full bg-black/70 p-3 text-white shadow-lg backdrop-blur-sm transition-colors hover:bg-black"
              aria-label="Close artwork"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>

            {artwork.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={showPreviousArtwork}
                  className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-black/70 p-3 text-white shadow-lg backdrop-blur-sm transition-colors hover:bg-black"
                  aria-label="Previous artwork"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={showNextArtwork}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-black/70 p-3 text-white shadow-lg backdrop-blur-sm transition-colors hover:bg-black"
                  aria-label="Next artwork"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </button>
              </>
            )}

            <div className="absolute inset-x-3 bottom-3 flex flex-col gap-2 sm:inset-x-6 sm:bottom-6 sm:flex-row sm:items-end sm:justify-between">
              <ArtworkCredit artwork={selectedArtwork} className="session-gallery-credit-lightbox" />
              <div className="self-start rounded-full bg-black/75 px-3 py-1.5 text-xs font-medium text-white/80 shadow-lg backdrop-blur-md sm:self-auto">
                {selectedIndex + 1} of {artwork.length}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export function SessionEndedPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { getToken, isSignedIn, userId } = useAuth();
  const { t } = useCopy();
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'financial'>('overview');
  const [financialSummary, setFinancialSummary] = useState<EventFinancialSummary | null>(null);
  const [financialLoading, setFinancialLoading] = useState(false);
  const [financialError, setFinancialError] = useState<string | null>(null);
  const [financialNotice, setFinancialNotice] = useState<string | null>(null);
  const [refundingPaymentId, setRefundingPaymentId] = useState<string | null>(null);
  const [settlingPayouts, setSettlingPayouts] = useState(false);
  const [loading, setLoading] = useState(true);
  const [purchasingProductId, setPurchasingProductId] = useState<string | null>(null);
  const [requestingProductId, setRequestingProductId] = useState<string | null>(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateDateTime, setDuplicateDateTime] = useState('');
  const [duplicating, setDuplicating] = useState(false);
  const [releaseDetailsByProductId, setReleaseDetailsByProductId] = useState<Record<string, ProductReleaseDetails>>({});
  const [releaseRequestProduct, setReleaseRequestProduct] = useState<SessionProduct | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [safetyCheckCleared, setSafetyCheckCleared] = useState(false);

  useEffect(() => {
    setSafetyCheckCleared(false);
  }, [code, userId]);

  useEffect(() => {
    const fetchSessionData = async () => {
      if (!code) return;

      try {
        const token = isSignedIn ? await getToken() : null;
        const requestAuthHeaders = authHeaders(token);

        // Fetch room data including host and model profiles
        const response = await fetch(`/api/rooms/${code}/invitation`, { headers: requestAuthHeaders });
        if (!response.ok) {
          if (response.status === 404) {
            setError('Session not found');
          } else {
            setError('Failed to load session data');
          }
          setLoading(false);
          return;
        }

        const data = await response.json();

        // Fetch artwork for the embedded summary gallery
        let artwork: SessionArtwork[] = [];
        let artworkCount = 0;
        try {
          const artResponse = await fetch(`/api/rooms/${code}/artwork`);
          if (artResponse.ok) {
            const artData = await artResponse.json();
            artwork = artData.artwork || [];
            artworkCount = artwork.length;
          }
        } catch {
          // Ignore artwork fetch errors
        }

        // Fetch recordings with current user's playback access
        let recordings: SecureRecording[] = [];
        try {
          const recordingsResponse = await fetch(`/api/recordings/${code}/access`, { headers: requestAuthHeaders });
          if (recordingsResponse.ok) {
            const recordingsData = await recordingsResponse.json();
            recordings = recordingsData.recordings || [];
          }
        } catch {
          // Ignore recordings fetch errors
        }

        let products: SessionProduct[] = [];
        try {
          const productsResponse = await fetch(`/api/products/reference-packs/sessions/${code}/summary`, { headers: requestAuthHeaders });
          if (productsResponse.ok) {
            const productsData = await productsResponse.json();
            products = productsData.products || [];
          }
        } catch {
          // Ignore product fetch errors
        }

        // Map API response to Profile type
        const mapToProfile = (p: any): Profile | null => {
          if (!p) return null;
          return {
            id: p.id,
            authUserId: p.authUserId || p.userId || '',
            userId: p.authUserId || p.userId || '',
            displayName: p.displayName,
            username: p.username || null,
            bio: p.bio || null,
            instagram: p.instagram || null,
            website: p.website || null,
            country: p.country || null,
            isModel: p.isModel || false,
            isArtist: p.isArtist || false,
            isHost: p.isHost || false,
            canModelPortraiture: p.canModelPortraiture || false,
            canModelNude: p.canModelNude || false,
            modelingStyles: p.modelingStyles || [],
            languages: p.languages || [],
            applicationStatus: p.applicationStatus || null,
            stripeAccountId: p.stripeAccountId || null,
            stripeOnboardingComplete: p.stripeOnboardingComplete || false,
            stripeCustomerId: p.stripeCustomerId || null,
            defaultShowIdentity: p.defaultShowIdentity ?? true,
            cameraEquipment: p.cameraEquipment || null,
            photos: p.photos || [],
            spacePhotos: p.spacePhotos || [],
            artworkPhotos: p.artworkPhotos || [],
            createdAt: p.createdAt || '',
            updatedAt: p.updatedAt || '',
          };
        };

        setSessionData({
          room: data,
          hostProfile: mapToProfile(data.hostProfile),
          modelProfile: mapToProfile(data.modelProfile),
          modelProfiles: Array.isArray(data.modelProfiles)
            ? data.modelProfiles.map(mapToProfile).filter((profile: Profile | null): profile is Profile => Boolean(profile))
            : data.modelProfile
              ? [mapToProfile(data.modelProfile)].filter((profile: Profile | null): profile is Profile => Boolean(profile))
              : [],
          attendees: Array.isArray(data.attendees) ? data.attendees : [],
          viewerIsHost: Boolean(data.isHost),
          viewerIsModel: Boolean(data.isModel),
          viewerHadTicket: Boolean(data.hasTicket) && !data.isHost && !data.isModel,
          artworkCount,
          artwork,
          recordings,
          products,
        });

        console.info('[SessionEndedPage] Loaded summary data', {
          code,
          signedIn: isSignedIn,
          modelProfile: data.modelProfile
            ? {
                id: data.modelProfile.id,
                userId: data.modelProfile.userId,
                authUserId: data.modelProfile.authUserId,
                displayName: data.modelProfile.displayName,
              }
            : null,
          hostProfile: data.hostProfile
            ? {
                id: data.hostProfile.id,
                userId: data.hostProfile.userId,
                authUserId: data.hostProfile.authUserId,
                displayName: data.hostProfile.displayName,
              }
            : null,
          recordingCount: recordings.length,
          productCount: products.length,
          artworkCount,
          attendeeCount: Array.isArray(data.attendees) ? data.attendees.length : 0,
          viewerIsModel: Boolean(data.isModel),
          viewerHadTicket: Boolean(data.hasTicket) && !data.isHost && !data.isModel,
        });
      } catch (error) {
        console.error('Error fetching session data:', error);
        setError('Failed to load session data');
      } finally {
        setLoading(false);
      }
    };

    fetchSessionData();
  }, [code, getToken, isSignedIn]);

  useEffect(() => {
    if (!sessionData || typeof window === 'undefined') return;

    const targetId = window.location.hash.slice(1);
    if (!targetId.startsWith('reference-pack-')) return;

    window.requestAnimationFrame(() => {
      document.getElementById(targetId)?.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    });
  }, [sessionData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-text-dim">Loading...</div>
      </div>
    );
  }

  if (error || !sessionData) {
    return (
      <div className="min-h-screen flex flex-col bg-bg pt-[72px]">
        <NavBar variant="light" />
        <main className="flex-1 flex items-center justify-center p-8">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-warn/10 border border-warn/30 flex items-center justify-center">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-warn">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4M12 16h.01" strokeLinecap="round" />
              </svg>
            </div>
            <h1 className="font-display text-2xl font-light text-text mb-2">
              {error || 'Session not found'}
            </h1>
            <p className="text-text-dim mb-6">
              This session may have been removed or the link is invalid.
            </p>
            <Link to="/" className="btn">
              Back to Home
            </Link>
          </div>
        </main>
        <SharedFooter />
      </div>
    );
  }

  const { room, hostProfile, modelProfile, modelProfiles, attendees, viewerIsHost, viewerIsModel, viewerHadTicket, artworkCount, artwork, recordings, products } = sessionData;
  const isCurrentUserModel = canCreateReferencePackFromSummary({ viewerIsModel, viewerIsHost });
  const canSeePayouts = viewerIsHost || viewerIsModel;
  const completedRecordings = recordings.filter(recording => recording.status === 'completed' && recording.hasVideo);
  const viewableRecordings = completedRecordings.filter(recording => recording.canView);
  const purchasableProducts = products.filter((product) =>
    !product.canEdit && product.status === 'ready' && product.accessType === 'sellable' && !product.purchased && (product.priceCents ?? 0) > 0
  );
  const purchasedProducts = products.filter((product) =>
    !product.canEdit && product.status === 'ready' && (product.purchased || product.accessType === 'free')
  );
  const editableProducts = products.filter((product) => product.canEdit);
  const displayedProducts = products;
  const accessiblePackRecordingIds = new Set(purchasedProducts.flatMap(product => product.recordingIds));
  const playableRecordings = viewableRecordings.filter(recording => accessiblePackRecordingIds.has(recording.id));
  const hasCreatedReferencePack = editableProducts.length > 0;
  const hasReleasedProducts = displayedProducts.length > 0;
  const sessionDateTime = room.scheduledAt
    ? new Date(room.scheduledAt).toLocaleString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: room.timezone || undefined,
        timeZoneName: room.timezone ? 'short' : undefined,
      })
    : null;
  const isSelfModeledSession = Boolean(hostProfile && modelProfile && hostProfile.id === modelProfile.id);
  const tipModelProfiles = modelProfiles.length > 0
    ? modelProfiles
    : modelProfile
      ? [modelProfile]
      : [];

  const handleDuplicateEvent = async () => {
    if (!code) return;

    if (!duplicateDateTime) {
      window.alert('Choose a new date and time for the duplicated event.');
      return;
    }

    const scheduledAt = new Date(duplicateDateTime);
    if (!Number.isFinite(scheduledAt.getTime()) || scheduledAt.getTime() <= Date.now()) {
      window.alert('Choose a future date and time for the duplicated event.');
      return;
    }

    setDuplicating(true);
    try {
      const token = await getToken();
      const response = await fetch(`/api/rooms/${code}/duplicate`, {
        method: 'POST',
        headers: jsonAuthHeaders(token),
        body: JSON.stringify({
          scheduledAt: scheduledAt.toISOString(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to duplicate event');
      }

      setShowDuplicateModal(false);
      setDuplicateDateTime('');
      navigate('/create', {
        state: {
          roomCode: data.room.code,
          section: 'details',
        },
      });
    } catch (err) {
      console.error('Error duplicating event:', err);
      window.alert(err instanceof Error ? err.message : 'Failed to duplicate event');
    } finally {
      setDuplicating(false);
    }
  };

  const loadFinancialSummary = async () => {
    if (!canSeePayouts) return;

    setFinancialLoading(true);
    setFinancialError(null);
    try {
      const token = await getToken();

      const response = await fetch(`/api/stripe/events/${room.id}/financial-summary`, {
        headers: authHeaders(token),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const authMessage = signInMessageForAuthStatus(response, 'Sign in to view financial information');
        if (authMessage) throw new Error(authMessage);
        throw new Error(data.error || 'Failed to load financial summary');
      }

      setFinancialSummary(data);
    } catch (err) {
      console.error('Error loading financial summary:', err);
      setFinancialError(err instanceof Error ? err.message : 'Failed to load financial summary');
    } finally {
      setFinancialLoading(false);
    }
  };

  const openFinancialTab = () => {
    setActiveTab('financial');
    if (!financialSummary && !financialLoading) {
      void loadFinancialSummary();
    }
  };

  const handleRefundPayment = async (payment: FinancialPayment) => {
    if (payment.status === 'refunded' || refundingPaymentId) return;
    if (!window.confirm(`Refund ${formatCents(payment.amount, payment.currency)} to ${payment.buyerName}?`)) {
      return;
    }

    setRefundingPaymentId(payment.id);
    setFinancialError(null);
    setFinancialNotice(null);
    try {
      const token = await getToken();

      const response = await fetch(`/api/stripe/events/${room.id}/payments/${payment.id}/refund`, {
        method: 'POST',
        headers: authHeaders(token),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const authMessage = signInMessageForAuthStatus(response, 'Sign in to refund ticket payments');
        if (authMessage) throw new Error(authMessage);
        throw new Error(data.error || 'Failed to refund payment');
      }

      await loadFinancialSummary();
    } catch (err) {
      console.error('Error refunding payment:', err);
      setFinancialError(err instanceof Error ? err.message : 'Failed to refund payment');
    } finally {
      setRefundingPaymentId(null);
    }
  };

  const handleSendPayouts = async () => {
    if (settlingPayouts) return;
    if (!financialSummary?.canSendPayouts) {
      setFinancialError(
        financialSummary?.hasBlockingRefundRequests
          ? 'Resolve pending refund requests before releasing payouts.'
          : financialSummary?.hasEnded
          ? 'There are no payouts ready to send for this event.'
          : 'Payouts can be sent after the event ends.'
      );
      return;
    }

    const retryingPayouts = financialSummary.payoutsStarted || financialSummary.payoutsPending || financialSummary.payoutsFailed;
    const confirmMessage = retryingPayouts
      ? 'Retry pending payouts for this event?'
      : 'Send payouts for this event? Refunds will be locked after payouts are sent.';
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setSettlingPayouts(true);
    setFinancialError(null);
    setFinancialNotice(null);
    try {
      const token = await getToken();

      const response = await fetch(`/api/stripe/settle/${room.id}`, {
        method: 'POST',
        headers: authHeaders(token),
      });
      const data = await response.json().catch(() => ({})) as SettlementResponse;
      if (!response.ok) {
        const authMessage = signInMessageForAuthStatus(response, 'Sign in to send payouts');
        if (authMessage) throw new Error(authMessage);
        throw new Error(data.error || 'Failed to send payouts');
      }

      const payoutStatuses = [data.hostPayoutStatus, data.modelPayoutStatus]
        .filter((status): status is Exclude<SettlementResponse['hostPayoutStatus'], undefined> => Boolean(status) && status !== 'none');
      if (payoutStatuses.includes('failed')) {
        setFinancialNotice('Some payouts could not be sent. Check the payout summary below for the Stripe message, then retry after the issue is fixed.');
      } else if (payoutStatuses.includes('pending')) {
        setFinancialNotice('Payouts were created but are pending. Recipients may need to finish Stripe payout setup before transfers can complete.');
      } else {
        setFinancialNotice('Payouts sent.');
      }

      await loadFinancialSummary();
    } catch (err) {
      console.error('Error sending payouts:', err);
      setFinancialError(err instanceof Error ? err.message : 'Failed to send payouts');
    } finally {
      setSettlingPayouts(false);
    }
  };

  const getProductDisplayPriceCents = (product: SessionProduct) => (
    product.accessRequest?.status === 'approved' && product.accessRequest.approvedPriceCents
      ? product.accessRequest.approvedPriceCents
      : product.priceCents ?? 0
  );

  const getProductActionLabel = (product: SessionProduct) => {
    const priceLabel = formatCents(getProductDisplayPriceCents(product), product.currency);

    if (!product.requiresBuyerApproval) return priceLabel;

    if (product.accessRequest?.status === 'approved') {
      return t('referencePacks.summary.buyForPrice', { price: priceLabel });
    }

    if (product.accessRequest?.status === 'pending') {
      return t('referencePacks.summary.requestPendingButton');
    }

    if (product.accessRequest?.status === 'denied') {
      return t('referencePacks.summary.requestAgainForPrice', { price: priceLabel });
    }

    return t('referencePacks.summary.requestAccessForPrice', { price: priceLabel });
  };

  const getProductAssetCountLabel = (product: SessionProduct) => t(
    'referencePacks.summary.recordingsAndPhotos',
    {
      recordingCount: product.recordingIds.length,
      recordingLabel: t(product.recordingIds.length === 1
        ? 'referencePacks.summary.recordingSingular'
        : 'referencePacks.summary.recordingPlural'),
      photoCount: product.captureIds.length,
      photoLabel: t(product.captureIds.length === 1
        ? 'referencePacks.summary.photoSingular'
        : 'referencePacks.summary.photoPlural'),
    }
  );

  console.info('[SessionEndedPage] Recording and reference-pack visibility', {
    code,
    signedIn: isSignedIn,
    currentUserId: userId,
    modelProfileId: modelProfile?.id ?? null,
    modelProfileUserId: modelProfile?.userId ?? null,
    modelProfileAuthUserId: modelProfile?.authUserId ?? null,
    hasModelProfile: Boolean(modelProfile),
    viewerIsModel,
    isCurrentUserModel,
    willShowPanel: isCurrentUserModel,
    rawRecordingCount: recordings.length,
    completedRecordingCount: completedRecordings.length,
    viewableRecordingCount: viewableRecordings.length,
    playableRecordingCount: playableRecordings.length,
    productCount: products.length,
    purchasableProductCount: purchasableProducts.length,
    purchasedProductCount: purchasedProducts.length,
    recordings: recordings.map((recording) => ({
      id: recording.id,
      label: recording.label,
      status: recording.status,
      hasVideo: recording.hasVideo,
      canView: recording.canView,
      releaseStatus: recording.releaseStatus,
      priceCents: recording.priceCents,
      startedAt: recording.startedAt,
      durationSeconds: recording.durationSeconds,
    })),
  });

  const handleBuyProduct = async (
    product: SessionProduct,
  ) => {
    if (!isSignedIn) {
      window.location.href = `/sign-in?redirect_url=/s/${code}/summary`;
      return;
    }

    if (product.requiresBuyerApproval && product.accessRequest?.status !== 'approved') {
      window.alert(t('errors.requestApprovalBeforePurchase'));
      return;
    }

    setPurchasingProductId(product.id);
    try {
      const token = await getToken();
      const response = await fetch(`/api/products/reference-packs/${product.id}/purchase`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          successUrl: `${window.location.origin}/dashboard/packs/${product.id}?payment=reference_pack_success&session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/s/${code}/summary`,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || t('referencePacks.requestAccess.failedToStartCheckout'));
      }

      const data = await response.json();
      window.location.href = data.url;
    } catch (err) {
      console.error('Error buying product:', err);
      window.alert(err instanceof Error ? err.message : t('referencePacks.requestAccess.failedToStartCheckout'));
    } finally {
      setPurchasingProductId(null);
    }
  };

  const handleRequestProduct = (product: SessionProduct) => {
    if (!isSignedIn) {
      window.location.href = `/sign-in?redirect_url=/s/${code}/summary`;
      return;
    }

    if (product.requiresBuyerApproval) {
      if (product.accessRequest?.status === 'approved') {
        void handleBuyProduct(product);
        return;
      }

      if (product.accessRequest?.status === 'pending') {
        window.alert(t('referencePacks.requestAccess.pendingAlert'));
        return;
      }

      setReleaseRequestProduct(product);
      return;
    }

    void handleBuyProduct(product);
  };

  const handleSubmitProductRequest = async (product: SessionProduct) => {
    if (!isSignedIn) {
      window.location.href = `/sign-in?redirect_url=/s/${code}/summary`;
      return;
    }

    const releaseDetails = releaseDetailsByProductId[product.id] ?? emptyProductReleaseDetails;
    const intendedUse = releaseDetails.intendedUse.trim();
    const requestedAccessExpiresOn = releaseDetails.accessExpiresOn.trim();

    if (!intendedUse) {
      window.alert(t('referencePacks.requestAccess.intendedUseRequiredAlert'));
      return;
    }

    setRequestingProductId(product.id);
    try {
      const token = await getToken();
      const response = await fetch(`/api/products/reference-packs/${product.id}/access-requests`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          intendedUse,
          requestedAccessExpiresOn: requestedAccessExpiresOn || null,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || t('referencePacks.requestAccess.failedToSendRequest'));
      }

      const data = await response.json();
      setSessionData((current) => current
        ? {
            ...current,
            products: current.products.map((existingProduct) => (
              existingProduct.id === product.id
                ? { ...existingProduct, accessRequest: data.request }
                : existingProduct
            )),
          }
        : current
      );
      setReleaseRequestProduct(null);
      window.alert(t('referencePacks.requestAccess.requestSentAlert'));
    } catch (err) {
      console.error('Error requesting reference pack access:', err);
      window.alert(err instanceof Error ? err.message : t('referencePacks.requestAccess.failedToSendRequest'));
    } finally {
      setRequestingProductId(null);
    }
  };

  const updateReleaseRequestDetails = (
    field: keyof ProductReleaseDetails,
    value: string,
  ) => {
    if (!releaseRequestProduct) return;

    const productId = releaseRequestProduct.id;
    setReleaseDetailsByProductId((prev) => ({
      ...prev,
      [productId]: {
        ...(prev[productId] ?? emptyProductReleaseDetails),
        [field]: value,
      },
    }));
  };

  const releaseRequestDetails = releaseRequestProduct
    ? releaseDetailsByProductId[releaseRequestProduct.id] ?? emptyProductReleaseDetails
    : emptyProductReleaseDetails;
  const releaseRequestComplete = Boolean(
    releaseRequestDetails.intendedUse.trim(),
  );

  if (viewerIsModel && isSignedIn && !safetyCheckCleared) {
    return (
      <div className="min-h-screen flex flex-col bg-bg pt-[72px]">
        <NavBar variant="light" />
        <main className="flex-1 flex items-center justify-center px-6 py-16">
          <ModelSafetyCheckGate
            roomCode={code}
            isSignedIn={isSignedIn}
            getToken={getToken}
            onCompleted={() => setSafetyCheckCleared(true)}
          />
        </main>
        <SharedFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-bg pt-[72px]">
      <NavBar variant="light" />

      {/* Content */}
      <main className="flex-1 py-12 px-8">
        <div className="max-w-[800px] mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            {room.imageUrl ? (
              <img
                src={room.imageUrl}
                alt={room.name}
                className="w-24 h-24 mx-auto mb-4 rounded-full object-cover border border-line shadow-sm"
              />
            ) : (
              <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-panel2 border border-line flex items-center justify-center text-text-dim text-3xl font-display">
                {room.name.charAt(0).toUpperCase()}
              </div>
            )}
            <h1 className="font-display text-3xl font-light text-text mb-2">
              Session Complete
            </h1>
            <div>
              <p className="font-display text-xl font-normal text-text">{room.name}</p>
              {sessionDateTime && (
                <p className="text-sm text-text-dim mt-1">{sessionDateTime}</p>
              )}
            </div>
          </div>

          {canSeePayouts && (
            <div className={`mb-8 flex flex-col sm:flex-row sm:items-center gap-3 ${viewerIsHost ? 'sm:justify-between' : 'justify-center'}`}>
              <div className="inline-flex rounded-lg border border-line bg-panel p-1">
                <button
                  type="button"
                  onClick={() => setActiveTab('overview')}
                  className={`px-4 py-2 rounded-md text-sm transition-colors ${
                    activeTab === 'overview'
                      ? 'bg-accent text-white'
                      : 'text-text-dim hover:text-text'
                  }`}
                >
                  Overview
                </button>
                <button
                  type="button"
                  onClick={openFinancialTab}
                  className={`px-4 py-2 rounded-md text-sm transition-colors ${
                    activeTab === 'financial'
                      ? 'bg-accent text-white'
                      : 'text-text-dim hover:text-text'
                  }`}
                >
                  Financial summary
                </button>
              </div>

              {viewerIsHost && (
                <button
                  type="button"
                  onClick={() => {
                    setDuplicateDateTime(getDefaultDuplicateDateTime(room.scheduledAt));
                    setShowDuplicateModal(true);
                  }}
                  disabled={duplicating}
                  className="btn btn-secondary"
                >
                  {duplicating ? 'Duplicating...' : 'Duplicate Event'}
                </button>
              )}
            </div>
          )}

          {activeTab === 'financial' && canSeePayouts ? (
            <FinancialSummaryPanel
              summary={financialSummary}
              loading={financialLoading}
              error={financialError}
              notice={financialNotice}
              viewerIsHost={viewerIsHost}
              viewerIsModel={viewerIsModel}
              refundingPaymentId={refundingPaymentId}
              settlingPayouts={settlingPayouts}
              onRefresh={loadFinancialSummary}
              onRefundPayment={handleRefundPayment}
              onSendPayouts={handleSendPayouts}
            />
          ) : (
            <>
              <PostSessionReputationPanel
                roomCode={code}
                isSignedIn={isSignedIn}
                getToken={getToken}
              />

          {isCurrentUserModel && !hasCreatedReferencePack && (
            <section className="bg-panel border border-line rounded-xl p-6 mb-8">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                <div>
                  <p className="text-[11px] uppercase text-text-dim mb-2">
                    Model next step
                  </p>
                  <h2 className="font-display text-xl font-normal">
                    Create your reference pack
                  </h2>
                  <p className="text-sm text-text-dim mt-1 leading-relaxed">
                    Raw recordings stay private until you choose what to include and publish a pack.
                  </p>
                </div>
                <Link to={`/s/${code}/packs`} className="btn btn-accent shrink-0">
                  Create Reference Pack
                </Link>
              </div>
            </section>
          )}

          {tipModelProfiles.length > 0 && viewerHadTicket && (
            <div id="tip-model" className="max-w-[760px] mx-auto mb-8">
              {isSignedIn ? (
                <TipForms
                  models={tipModelProfiles.map((profile) => ({
                    id: profile.id,
                    displayName: profile.displayName,
                    photoUrl: profile.photos?.[0]?.url ?? null,
                  }))}
                  roomId={room.id}
                  currency={room.currency}
                />
              ) : (
                <div className="bg-panel border border-line rounded-xl p-5 text-center">
                  <h3 className="font-display text-xl font-normal">Tip the model{tipModelProfiles.length === 1 ? '' : 's'}</h3>
                  <p className="text-sm text-text-dim mt-1 mb-4">
                    Sign in to charge your card and send a tip.
                  </p>
                  <Link to={`/sign-in?redirect_url=/s/${code}/summary`} className="btn btn-accent w-full">
                    Sign in to tip
                  </Link>
                </div>
              )}
            </div>
          )}

          {artworkCount > 0 && (
            <SessionGallerySummary artwork={artwork} />
          )}

          <AttendeesSummary attendees={attendees} />

          <div id="session-recordings" className="bg-panel border border-line rounded-xl p-6 mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-5">
              <div className="flex-1">
                <h2 className="font-display text-xl font-normal">
                  {t('referencePacks.summary.sectionTitle')}
                </h2>
                <p className="text-sm text-text-dim mt-1">
                  {playableRecordings.length > 0
                    ? t('referencePacks.summary.includedRecordingsAccessible')
                    : editableProducts.length > 0
                      ? t('referencePacks.summary.yourPacksAvailable')
                      : purchasableProducts.length > 0
                        ? t('referencePacks.summary.modelReleasedPacks')
                      : purchasedProducts.length > 0
                        ? t('referencePacks.summary.purchasedPacksAccessible')
                      : hasReleasedProducts
                        ? t('referencePacks.summary.packsAvailable')
                        : t('referencePacks.summary.noPacksAvailable')}
                </p>
              </div>
            </div>

            {playableRecordings.length > 0 && (
              <div className="space-y-4">
                {playableRecordings.map((recording) => (
                  <SecureVideoPlayer
                    key={recording.id}
                    recordingId={recording.id}
                    label={recording.label}
                    hasVideo={recording.hasVideo}
                    contentType={recording.contentType}
                    status={recording.status}
                    durationSeconds={recording.durationSeconds}
                    startedAt={recording.startedAt}
                  />
                ))}
              </div>
            )}

            {displayedProducts.length > 0 && (
              <div className="space-y-3 mt-4">
                {displayedProducts.map((product) => {
                  const isPurchased = Boolean(product.purchased);
                  const isFree = product.accessType === 'free';
                  const accessExpiresOn = product.purchaseAccessExpiresOn ?? product.accessExpiresOn;
                  const accessDurationLabel = getReferencePackAccessDurationLabel(product.accessDurationCount, product.accessDurationUnit);
                  return (
                    <div
                      key={product.id}
                      id={`reference-pack-${product.id}`}
                      className="scroll-mt-28 rounded-lg bg-bg-secondary border border-line-soft p-4"
                    >
                      <ReferencePackPhotoMasonry product={product} />
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                        <div className="min-w-0">
                          <div className="font-medium text-sm text-text">{product.title}</div>
                          {product.description && (
                            <div className="text-xs text-text-dim mt-1">{product.description}</div>
                          )}
                          <div className="text-xs text-text-faint mt-1">
                            {getProductAssetCountLabel(product)}
                          </div>
                          {product.requiresBuyerApproval && (
                            <div className="text-xs text-text-dim mt-2">
                              {t('referencePacks.summary.approvalExplainer', {
                                modelName: modelProfile?.displayName ?? 'The model',
                              })}
                            </div>
                          )}
                          {product.requiresBuyerApproval && product.accessRequest?.status === 'approved' && (
                            <div className="text-xs text-accent mt-2">
                              {t('referencePacks.summary.approvedFor', { price: formatCents(getProductDisplayPriceCents(product), product.currency) })}
                            </div>
                          )}
                          {product.requiresBuyerApproval && product.accessRequest?.status === 'pending' && (
                            <div className="text-xs text-text-dim mt-2">
                              {t('referencePacks.summary.requestPending')}
                            </div>
                          )}
                          {product.requiresBuyerApproval && product.accessRequest?.status === 'denied' && (
                            <div className="text-xs text-warn mt-2">
                              {product.accessRequest.sellerNote
                                ? t('referencePacks.summary.requestDeclinedWithNote', { note: product.accessRequest.sellerNote })
                                : t('referencePacks.summary.requestDeclined')}
                            </div>
                          )}
                          {accessExpiresOn && (
                            <div className="text-xs text-text-dim mt-1">
                              {t('referencePacks.summary.accessExpires', { date: accessExpiresOn })}
                            </div>
                          )}
                          {!accessExpiresOn && accessDurationLabel && (
                            <div className="text-xs text-text-dim mt-1">
                              Access for {accessDurationLabel} from purchase
                            </div>
                          )}
                        </div>
                        {product.canEdit ? (
                          <Link to={`/s/${code}/packs?product=${product.id}`} className="btn btn-secondary">
                            Edit
                          </Link>
                        ) : isPurchased ? (
                          <Link to={`/dashboard/packs/${product.id}`} className="btn btn-accent">
                            {t('referencePacks.summary.openReferencePack')}
                          </Link>
                        ) : isFree ? (
                          <span className="btn btn-sm cursor-default">{t('common.free')}</span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleRequestProduct(product)}
                            disabled={purchasingProductId === product.id || product.accessRequest?.status === 'pending'}
                            className="btn btn-accent disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {purchasingProductId === product.id
                              ? t('referencePacks.summary.openingCheckout')
                              : getProductActionLabel(product)}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* People Section */}
          <div className={`grid grid-cols-1 gap-6 mb-8 ${isSelfModeledSession ? '' : 'md:grid-cols-2'}`}>
            {isSelfModeledSession && hostProfile ? (
              <ProfileCard profile={hostProfile} role="host-model">
                <UpcomingSessionsList
                  profileId={hostProfile.id}
                  roles={['hosting', 'modeling']}
                />
              </ProfileCard>
            ) : (
              <>
                {/* Model Card */}
                {modelProfile && (
                  <ProfileCard profile={modelProfile} role="model">
                    <UpcomingSessionsList
                      profileId={modelProfile.id}
                      roles={['modeling']}
                      title="Upcoming modeling sessions"
                    />
                  </ProfileCard>
                )}

                {/* Host Card */}
                {hostProfile && (
                  <ProfileCard profile={hostProfile} role="host">
                    <UpcomingSessionsList
                      profileId={hostProfile.id}
                      roles={['hosting']}
                      title="Upcoming organized sessions"
                    />
                  </ProfileCard>
                )}
              </>
            )}
          </div>

            </>
          )}

        </div>
      </main>

      {releaseRequestProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="release-request-title"
            className="w-full max-w-lg rounded-xl border border-line bg-panel p-5 shadow-xl"
          >
            <div className="mb-5">
              <h2 id="release-request-title" className="font-display text-[28px] leading-tight text-text">
                {t('referencePacks.requestAccess.modalTitle')}
              </h2>
              <p className="mt-2 text-sm text-text-dim">
                {t('referencePacks.requestAccess.modalBody')}
              </p>
            </div>

            <div className="mb-5 rounded-lg bg-bg-secondary border border-line-soft p-4">
              <div className="font-medium text-sm text-text">{releaseRequestProduct.title}</div>
              {releaseRequestProduct.description && (
                <div className="mt-1 text-xs text-text-dim">{releaseRequestProduct.description}</div>
              )}
              <div className="mt-2 text-xs text-text-faint">
                {getProductAssetCountLabel(releaseRequestProduct)} - {' '}
                {t('referencePacks.requestAccess.listedAt', { price: formatCents(releaseRequestProduct.priceCents ?? 0, releaseRequestProduct.currency) })}
              </div>
            </div>

            <div className="grid gap-4">
              <label className="block">
                <span className="text-xs font-medium text-text-dim">{t('referencePacks.requestAccess.intendedUseLabel')}</span>
                <textarea
                  value={releaseRequestDetails.intendedUse}
                  onChange={(event) => updateReleaseRequestDetails('intendedUse', event.target.value)}
                  className="input mt-1 min-h-[110px] h-auto py-3 text-sm resize-y"
                  placeholder={t('referencePacks.requestAccess.intendedUsePlaceholder')}
                />
              </label>

              <label className="block">
                <span className="text-xs font-medium text-text-dim">
                  {t('referencePacks.requestAccess.requestedAccessUntilLabel')}{' '}
                  <span className="font-normal text-text-faint">({t('common.optional')})</span>
                </span>
                <input
                  type="date"
                  value={releaseRequestDetails.accessExpiresOn}
                  onChange={(event) => updateReleaseRequestDetails('accessExpiresOn', event.target.value)}
                  min={new Date().toISOString().slice(0, 10)}
                  className="input mt-1 h-11 text-sm"
                />
                <p className="mt-1 text-[11px] leading-snug text-text-faint">
                  {t('referencePacks.requestAccess.requestedAccessUntilHelp')}
                </p>
              </label>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setReleaseRequestProduct(null)}
                disabled={requestingProductId === releaseRequestProduct.id}
                className="btn disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={() => handleSubmitProductRequest(releaseRequestProduct)}
                disabled={requestingProductId === releaseRequestProduct.id || !releaseRequestComplete}
                className="btn btn-accent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {requestingProductId === releaseRequestProduct.id
                  ? t('referencePacks.requestAccess.sendingRequest')
                  : t('referencePacks.requestAccess.sendRequest')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDuplicateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4">
          <div className="w-full max-w-md rounded-xl border border-line bg-panel p-5 shadow-xl">
            <h2 className="font-display text-[28px] leading-tight text-text">Duplicate event</h2>
            <label className="mt-4 block">
              <span className="text-[13px] font-medium text-text">New date and time</span>
              <div className="mt-1">
                <DateTimePicker
                  value={duplicateDateTime}
                  onChange={setDuplicateDateTime}
                  timezone={Intl.DateTimeFormat().resolvedOptions().timeZone}
                  placeholder="Start time"
                  minDateTime={getDuplicateDateTimeMin()}
                />
              </div>
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowDuplicateModal(false)}
                disabled={duplicating}
                className="btn disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDuplicateEvent}
                disabled={duplicating || !duplicateDateTime}
                className="btn btn-accent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {duplicating ? 'Duplicating...' : 'Duplicate Event'}
              </button>
            </div>
          </div>
        </div>
      )}

      <SharedFooter />
    </div>
  );
}
